/** @format */
import React, { FunctionComponent } from 'react';

import { getConfig } from '@toucan/common/services/nextjs/settings';

import { logger } from '@toucan/client/global/application';
import { CloudflareImageSize } from '@toucan/client/global/cloudflare';

import { useFallback } from '@toucan/client/shared/hooks/use-fallback';
import {
    ResponsiveLoaderType,
    getCloudflareResizeServiceUrl,
    useResponsiveLoader,
} from '@toucan/client/shared/hooks/use-responsive-loader';

import { CloudflareImageProps, ImageSourceSet } from './types';

export const CloudflareImage: FunctionComponent<CloudflareImageProps> = ({
    fallbackSrc,
    src,
    imageSize = CloudflareImageSize.THUMBNAIL,
    alt = '',
    decoding = 'async',
    loading = 'lazy',
    ...otherProps
}) => {
    const { isLoaderEnabled: isCloudflareEnabled } = useResponsiveLoader(
        ResponsiveLoaderType.CLOUDFLARE
    );

    const { publicRuntimeConfig } = getConfig();

    const isLoggedEnvironment = !publicRuntimeConfig?.dev;

    const cloudflareSrcSet: ImageSourceSet = [
        {
            src: getCloudflareResizeServiceUrl({
                src,
                width: imageSize,
                format: 'jpeg',
            }),
            density: '1x',
        },
        {
            src: getCloudflareResizeServiceUrl({
                src,
                width: imageSize * 2,
                format: 'jpeg',
            }),
            density: '2x',
        },
    ];

    const basicSrcSet: ImageSourceSet = [{ src, density: '1x' }];

    const fallbackSrcSet: ImageSourceSet | undefined = fallbackSrc
        ? [{ src: fallbackSrc, density: '1x' }]
        : undefined;

    const getFallbackSources = (): ImageSourceSet[] => {
        const sources = [];

        if (isCloudflareEnabled) {
            sources.push(cloudflareSrcSet);
        }

        sources.push(basicSrcSet);

        if (fallbackSrcSet) {
            sources.push(fallbackSrcSet);
        }

        return sources;
    };

    const fallbackSources = getFallbackSources();

    const { currentSource, handleError } = useFallback<ImageSourceSet>(
        fallbackSources,
        // We want to log when product image fails, not wait until 'placeholder' is shown.
        ({ source, fallbacks, attempt }) => {
            if (isLoggedEnvironment && attempt === fallbackSources.length - 1) {
                logger.addLabels({
                    mpb_attempt: attempt,
                    mpb_current_source: source[0]?.src,
                    mpb_fallbacks: fallbacks
                        .map((fallback) => fallback[0]?.src)
                        .join(', '),
                });

                logger.error('Image failed to load');
            }
        }
    );

    if (!currentSource?.[0]?.src) {
        return null;
    }

    const srcSet = currentSource
        .map(({ src, density }) => `${src} ${density}`)
        .join(',');

    return (
        <img
            {...otherProps}
            src={currentSource[0].src}
            srcSet={srcSet}
            sizes={`${imageSize}px`}
            alt={alt}
            decoding={decoding}
            loading={loading}
            onError={handleError}
        />
    );
};
